import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Button, Grid, TextField, FormControl, InputLabel, Select, MenuItem,
  Checkbox, FormControlLabel, FormGroup, CircularProgress, Snackbar, Alert, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination,
  Chip, Card, CardContent, Dialog, DialogTitle, DialogContent, DialogActions,
  ToggleButton, ToggleButtonGroup, LinearProgress, Tooltip, Badge, Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon, Refresh as RefreshIcon, Download as DownloadIcon,
  Warning as WarningIcon, Assessment as AssessmentIcon, FilterList as FilterListIcon, ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { getFinanceFilterOptions, getFinanceGstReport } from '../api/adminPanelApi';
import {
  DEFAULT_EXCEL_SECTIONS, DEFAULT_INCLUDE_SECTIONS, downloadFinanceExcel, EXCEL_SECTION_LABELS,
} from '../utils/financeExport';

const RESERVED_FILTER_KEYS = new Set(['cancellation_mode', 'include_test_bookings', 'include_pay_at_hotel']);

const formatCurrency = (n) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(Number(n) || 0);

const getCurrentMonth = () => new Date().toISOString().slice(0, 7);
const monthStartDate = (m) => `${m}-01`;
const monthEndDate = (monthStr) => {
  const [y, mo] = monthStr.split('-').map(Number);
  const last = new Date(y, mo, 0).getDate();
  const now = new Date();
  return monthStr === now.toISOString().slice(0, 7) ? now.toISOString().slice(0, 10) : `${monthStr}-${String(last).padStart(2, '0')}`;
};
const monthsToDateRange = (s, e) => ({ start: monthStartDate(s), end: monthEndDate(e) });

const FALLBACK_DATE_FIELDS = [
  { value: 'created_at', label: 'Booking created' },
  { value: 'payment_date', label: 'Payment date' },
  { value: 'check_in_date', label: 'Check-in' },
  { value: 'check_out_date', label: 'Check-out' },
  { value: 'settlement_transfer_date', label: 'Settlement transfer' },
];

const CANCELLATION_MODES = [
  { value: 'exclude', label: 'Exclude cancelled' },
  { value: 'include', label: 'Include cancelled' },
  { value: 'only', label: 'Cancelled only' },
];

const SECTION_TABS = [
  { key: 'tabtrips_files_room_gst', label: 'TabTrips files GST', short: 'TT files' },
  { key: 'property_files_room_gst', label: 'Property files GST', short: 'Property' },
  { key: 'international', label: 'International', short: 'Intl' },
];

const INCLUDE_LABELS = {
  booking_sections: 'Booking sections (3 GST tables)',
  calculations: 'GST summary totals',
  settlements: 'Settlements',
  wrong_calculations_report: 'Wrong calculation audit',
  monthly_profit: 'Monthly profit',
  cancellation_loss: 'Cancellation loss',
  gst_claim_on_cancellations: 'GST claim on cancellations',
  checkout_in_period: 'Checkout in period',
  future_bookings: 'Future bookings',
};

const KPI_DEFS = [
  { path: 'sum_final_amount_paid', label: 'Guest collections', color: '#1565c0' },
  { path: 'sum_gst_amount', label: 'Total room GST', color: '#2e7d32' },
  { path: 'gst_filing.room_gst_filed_by_property', label: 'Room GST (property)', color: '#388e3c' },
  { path: 'gst_filing.room_gst_filed_by_tabtrips', label: 'Room GST (TabTrips)', color: '#00695c' },
  { path: 'sum_tabtrips_commission', label: 'Commission', color: '#ef6c00' },
  { path: 'sum_gst_on_tabtrips_comm', label: 'GST on commission', color: '#ad1457' },
  { path: 'razorpay.total_razorpay_charge_all', label: 'Razorpay charges', color: '#6a1b9a' },
  { path: 'tabtrips_profit.commission', label: 'TabTrips profit', color: '#0d47a1', altPath: 'tabtrips_profit' },
];

const getNested = (obj, path) => path.split('.').reduce((o, k) => (o == null ? undefined : o[k]), obj);

const emptySelectedFilters = () => ({
  settlement_status: [],
  booking_status: [],
  hotel_ids: [],
});

const FinanceReport = () => {
  const navigate = useNavigate();
  const currentMonth = getCurrentMonth();
  const initialRange = monthsToDateRange(currentMonth, currentMonth);

  const [periodMode, setPeriodMode] = useState('month');
  const [startMonth, setStartMonth] = useState(currentMonth);
  const [endMonth, setEndMonth] = useState(currentMonth);
  const [startDate, setStartDate] = useState(initialRange.start);
  const [endDate, setEndDate] = useState(initialRange.end);
  const [dateField, setDateField] = useState('created_at');
  const [cancellationMode, setCancellationMode] = useState('exclude');
  const [includePayAtHotel, setIncludePayAtHotel] = useState(false);
  const [calcView, setCalcView] = useState('excluding');

  const [filterCatalog, setFilterCatalog] = useState(null);
  const [includeSections, setIncludeSections] = useState({ ...DEFAULT_INCLUDE_SECTIONS });
  const [selectedFilters, setSelectedFilters] = useState(emptySelectedFilters);
  const [draftFilters, setDraftFilters] = useState(emptySelectedFilters);
  const [draftInclude, setDraftInclude] = useState({ ...DEFAULT_INCLUDE_SECTIONS });
  const [draftPayAtHotel, setDraftPayAtHotel] = useState(false);

  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [report, setReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [excelDialogOpen, setExcelDialogOpen] = useState(false);
  const [excelSections, setExcelSections] = useState({ ...DEFAULT_EXCEL_SECTIONS });

  const [activeSection, setActiveSection] = useState('tabtrips_files_room_gst');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const dateFields = useMemo(() => {
    const items = normalizeOptions(filterCatalog?.available_filters?.date_fields);
    return items.length ? items : FALLBACK_DATE_FIELDS;
  }, [filterCatalog]);

  const optionalFilterDefs = useMemo(() => {
    const raw = filterCatalog?.available_filters?.filters || [];
    if (!Array.isArray(raw)) return [];
    return raw
      .map((fd, i) => ({
        key: fd.key ?? fd.id ?? `filter_${i}`,
        label: fd.label ?? fd.key ?? 'Filter',
        options: fd.options ?? fd.values ?? [],
      }))
      .filter((fd) => !RESERVED_FILTER_KEYS.has(fd.key));
  }, [filterCatalog]);

  const activeFilterCount = useMemo(() => {
    let n = includePayAtHotel ? 1 : 0;
    Object.values(selectedFilters).forEach((arr) => { n += (arr || []).length; });
    return n;
  }, [selectedFilters, includePayAtHotel]);

  const loadFilters = useCallback(async () => {
    try {
      const res = await getFinanceFilterOptions();
      if (res.success) setFilterCatalog(res.data);
    } catch { /* fallback */ }
  }, []);

  useEffect(() => { loadFilters(); }, [loadFilters]);

  const buildPayload = (overrides = {}) => {
    const filters = {
      cancellation_mode: String(cancellationMode),
      include_test_bookings: false,
      include_pay_at_hotel: Boolean(includePayAtHotel),
    };
    Object.entries(selectedFilters).forEach(([k, v]) => {
      if (RESERVED_FILTER_KEYS.has(k)) return;
      const cleaned = (Array.isArray(v) ? v : []).filter((x) => x != null && x !== '');
      if (cleaned.length > 0) filters[k] = cleaned;
    });

    const include = { booking_sections: true };
    Object.entries(includeSections).forEach(([k, on]) => {
      if (on && k !== 'booking_sections') include[k] = true;
    });

    return {
      start_date: startDate,
      end_date: endDate,
      date_field: dateField,
      page: 1,
      limit: rowsPerPage,
      filters,
      include,
      ...overrides,
    };
  };

  const fetchReport = async (pageNum = 1, limit = rowsPerPage) => {
    if (!startDate || !endDate) {
      setSnackbar({ open: true, message: 'Select a period', severity: 'warning' });
      return;
    }
    if (startDate > endDate) {
      setSnackbar({ open: true, message: 'Start cannot be after end', severity: 'warning' });
      return;
    }
    setLoadingReport(true);
    try {
      const res = await getFinanceGstReport(buildPayload({ page: pageNum, limit }));
      if (res.success) {
        setReport(res.data);
        setPage(pageNum - 1);
      } else {
        setSnackbar({ open: true, message: res.error || 'Failed to load report', severity: 'error' });
      }
    } catch (e) {
      setSnackbar({ open: true, message: e.message || 'Failed', severity: 'error' });
    } finally {
      setLoadingReport(false);
    }
  };

  const refetchPage = async (pageNum, limit) => {
    setLoadingReport(true);
    try {
      const res = await getFinanceGstReport(buildPayload({ page: pageNum, limit }));
      if (res.success) setReport(res.data);
    } finally {
      setLoadingReport(false);
    }
  };

  const applyMonthRange = (from, to) => {
    const r = monthsToDateRange(from, to);
    setStartDate(r.start);
    setEndDate(r.end);
  };

  const activeCalculations = useMemo(() => {
    const calc = report?.calculations;
    if (!calc) return null;
    if (cancellationMode === 'include' && calcView === 'including' && calc.totals_including_cancellations) {
      return { ...calc, ...calc.totals_including_cancellations };
    }
    if (calc.totals_excluding_cancellations) return { ...calc, ...calc.totals_excluding_cancellations };
    return calc;
  }, [report, cancellationMode, calcView]);

  const sectionBlock = report?.booking_sections?.[activeSection];
  const sectionBookings = sectionBlock?.bookings || [];
  const sectionTotal = sectionBlock?.count ?? report?.meta?.section_counts?.[activeSection] ?? sectionBookings.length;
  const wrongBookings = report?.wrong_calculations?.bookings || [];
  const settled = report?.settlements?.settled_bookings || [];

  const fetchFullReportForExport = async () => {
    const res = await getFinanceGstReport(buildPayload({ page: 1, limit: 5000 }));
    if (!res.success) throw new Error(res.error || 'Export failed');
    return res.data;
  };

  const handleExcelExport = async () => {
    if (!report) return;
    setExporting(true);
    try {
      const data = excelSections.booking_sections ? await fetchFullReportForExport() : report;
      downloadFinanceExcel(data, excelSections, { start_date: startDate, end_date: endDate });
      setExcelDialogOpen(false);
      setSnackbar({ open: true, message: 'Excel downloaded', severity: 'success' });
    } catch (e) {
      setSnackbar({ open: true, message: e.message || 'Export failed', severity: 'error' });
    } finally {
      setExporting(false);
    }
  };

  const renderBookingRows = (rows) => rows.map((b) => {
    const wrong = b.wrong_calculation?.is_wrong;
    return (
      <TableRow key={b.booking_id || b.booking_code} hover sx={wrong ? { bgcolor: '#fffde7' } : undefined}>
        <TableCell>
          <Typography variant="body2" fontWeight={600}>{b.booking_code}</Typography>
          <Typography variant="caption" color="text.secondary">{b.guest_name || '–'}</Typography>
          {wrong && (
            <Tooltip title={(b.wrong_calculation?.issues || []).join(', ')}>
              <Chip size="small" label="Audit" color="warning" sx={{ mt: 0.5, height: 18, fontSize: 10 }} />
            </Tooltip>
          )}
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{b.hotel_name}</Typography>
          <Typography variant="caption" color="text.secondary">{b.hotel_location}</Typography>
        </TableCell>
        <TableCell>
          <Typography variant="caption" display="block">{b.check_in_date} → {b.check_out_date}</Typography>
          <Typography variant="caption" color="text.secondary">{b.total_nights ?? '–'} night(s)</Typography>
        </TableCell>
        <TableCell>
          <Typography variant="caption">{b.gst_number || '–'}</Typography>
          <Typography variant="caption" display="block">{formatCurrency(b.room_gst_amount)} @ {b.room_gst_rate_percent}%</Typography>
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{b.commission_and_gst?.display || `${formatCurrency(b.commission)} + GST`}</Typography>
        </TableCell>
        <TableCell align="right">{formatCurrency(b.final_amount_paid)}</TableCell>
        <TableCell align="right">{formatCurrency(b.razorpay_charge)}</TableCell>
        <TableCell align="right">{formatCurrency(b.final_pay_to_hotel)}</TableCell>
        <TableCell>
          <Chip size="small" label={b.settlement?.status || 'pending'} color={b.settlement?.status === 'transferred' ? 'success' : 'default'} sx={{ height: 20, fontSize: '0.65rem' }} />
        </TableCell>
      </TableRow>
    );
  });

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: '#f4f6f8', minHeight: '100vh' }}>
      {(loadingReport || exporting) && <LinearProgress sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1200 }} />}

      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <IconButton onClick={() => navigate('/')} size="small" sx={{ color: '#FF7700' }}><ArrowBackIcon /></IconButton>
          <Box>
            <Typography variant="h5" fontWeight={700} sx={{ color: '#2E4057' }}>Finance & GST</Typography>
            <Typography variant="caption" color="text.secondary">Monthly filing · online Razorpay default</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={() => setExcelDialogOpen(true)} disabled={!report || exporting}>Export Excel</Button>
          <Button variant="contained" size="small" startIcon={loadingReport ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />} onClick={() => fetchReport(1, rowsPerPage)} disabled={loadingReport} sx={{ bgcolor: '#FF7700', '&:hover': { bgcolor: '#e65100' } }}>Generate Report</Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md="auto">
            <ToggleButtonGroup size="small" exclusive value={periodMode} onChange={(_, m) => { if (m) { setPeriodMode(m); if (m === 'month') applyMonthRange(startMonth, endMonth); } }}>
              <ToggleButton value="month">Month range</ToggleButton>
              <ToggleButton value="custom">Custom dates</ToggleButton>
            </ToggleButtonGroup>
          </Grid>
          {periodMode === 'month' ? (
            <>
              <Grid item xs={6} sm={3} md={2}><TextField fullWidth size="small" label="Start month" type="month" value={startMonth} onChange={(e) => { const v = e.target.value; setStartMonth(v); if (v > endMonth) setEndMonth(v); applyMonthRange(v, v > endMonth ? v : endMonth); }} InputLabelProps={{ shrink: true }} inputProps={{ max: currentMonth }} /></Grid>
              <Grid item xs={6} sm={3} md={2}><TextField fullWidth size="small" label="End month" type="month" value={endMonth} onChange={(e) => { const v = e.target.value; if (v < startMonth) { setSnackbar({ open: true, message: 'End month before start', severity: 'warning' }); return; } setEndMonth(v); applyMonthRange(startMonth, v); }} InputLabelProps={{ shrink: true }} inputProps={{ max: currentMonth, min: startMonth }} /></Grid>
            </>
          ) : (
            <>
              <Grid item xs={6} sm={3} md={2}><TextField fullWidth size="small" label="From" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} /></Grid>
              <Grid item xs={6} sm={3} md={2}><TextField fullWidth size="small" label="To" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }} /></Grid>
            </>
          )}
          <Grid item xs={12} sm={4} md={2}>
            <FormControl fullWidth size="small"><InputLabel shrink>Date field</InputLabel>
              <Select value={dateField} label="Date field" onChange={(e) => setDateField(e.target.value)}>{dateFields.map((f) => <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>)}</Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <FormControl fullWidth size="small"><InputLabel shrink>Cancellations</InputLabel>
              <Select value={cancellationMode} label="Cancellations" onChange={(e) => setCancellationMode(e.target.value)}>{CANCELLATION_MODES.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}</Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md="auto">
            <FormControlLabel control={<Checkbox size="small" checked={includePayAtHotel} onChange={(e) => setIncludePayAtHotel(e.target.checked)} />} label={<Typography variant="body2">Include Pay at Hotel</Typography>} />
          </Grid>
          <Grid item xs={12} md="auto">
            <Badge badgeContent={activeFilterCount} color="primary">
              <Button variant="outlined" size="small" startIcon={<FilterListIcon />} onClick={() => { setDraftFilters({ ...selectedFilters }); setDraftInclude({ ...includeSections }); setDraftPayAtHotel(includePayAtHotel); setFilterDialogOpen(true); }}>Filters</Button>
            </Badge>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">
              {startDate} → {endDate} · {!includePayAtHotel && <Chip label="Online (Razorpay) only" size="small" sx={{ height: 20, ml: 0.5 }} />}
              {report?.meta?.total_bookings_matching != null && <> · <strong>{report.meta.total_bookings_matching}</strong> bookings match</>}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {!report && !loadingReport && (
        <Paper sx={{ p: 5, textAlign: 'center' }}><AssessmentIcon sx={{ fontSize: 48, color: '#ccc', mb: 1 }} /><Typography color="text.secondary">Set period and click <strong>Generate Report</strong>.</Typography></Paper>
      )}

      {report && (
        <>
          {cancellationMode === 'include' && report.calculations?.totals_including_cancellations && (
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <ToggleButtonGroup size="small" exclusive value={calcView} onChange={(_, v) => v && setCalcView(v)}>
                <ToggleButton value="excluding">Excl. cancellations</ToggleButton>
                <ToggleButton value="including">Incl. cancellations</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}

          {activeCalculations && (
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              {KPI_DEFS.map(({ path, altPath, label, color }) => {
                let val = getNested(activeCalculations, path);
                if (val == null && altPath) val = activeCalculations[altPath];
                return (
                  <Grid item xs={6} sm={4} md={3} lg={1.5} key={path}>
                    <Card variant="outlined" sx={{ borderLeft: `3px solid ${color}`, height: '100%' }}>
                      <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
                        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2, display: 'block' }}>{label}</Typography>
                        <Typography variant="body2" fontWeight={700} sx={{ color, mt: 0.25 }}>{formatCurrency(val)}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}

          {settled.length > 0 && (
            <Accordion defaultExpanded={false} sx={{ mb: 2, borderRadius: '8px !important', '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography fontWeight={600}>Settled bookings ({settled.length})</Typography></AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <Table size="small">
                  <TableHead><TableRow><TableCell>Booking</TableCell><TableCell>Hotel</TableCell><TableCell align="right">Amount</TableCell><TableCell>Date</TableCell></TableRow></TableHead>
                  <TableBody>{settled.slice(0, 50).map((s, i) => (
                    <TableRow key={i}><TableCell>{s.booking_code || s.booking_id}</TableCell><TableCell>{s.hotel_name}</TableCell><TableCell align="right">{formatCurrency(s.hotel_amount)}</TableCell><TableCell>{s.transfer_date || '–'}</TableCell></TableRow>
                  ))}</TableBody>
                </Table>
              </AccordionDetails>
            </Accordion>
          )}

          <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={{ px: 2, py: 1, bgcolor: '#fafafa', borderBottom: '1px solid #eee' }}>
              <ToggleButtonGroup size="small" exclusive value={activeSection} onChange={(_, k) => { if (k) { setActiveSection(k); setPage(0); } }} sx={{ mb: 0.5 }}>
                {SECTION_TABS.map((t) => (
                  <ToggleButton key={t.key} value={t.key}>
                    {t.short} ({report?.meta?.section_counts?.[t.key] ?? report?.booking_sections?.[t.key]?.count ?? 0})
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              <Typography variant="caption" color="text.secondary" display="block">{sectionBlock?.label || SECTION_TABS.find((t) => t.key === activeSection)?.label}</Typography>
            </Box>
            <TableContainer sx={{ maxHeight: 1000 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Booking</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Hotel / Location</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Stay</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>GSTIN / Room GST</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Commission</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Guest paid</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Razorpay</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Pay hotel</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Settlement</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sectionBookings.length === 0 && <TableRow><TableCell colSpan={9} align="center" sx={{ py: 3 }}>No bookings in this section.</TableCell></TableRow>}
                  {renderBookingRows(sectionBookings)}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={sectionTotal}
              page={page}
              onPageChange={(_, p) => { setPage(p); refetchPage(p + 1, rowsPerPage); }}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { const l = parseInt(e.target.value, 10); setRowsPerPage(l); setPage(0); refetchPage(1, l); }}
              rowsPerPageOptions={[25, 50, 100, 200, 500]}
            />
          </Paper>

          {(report.wrong_calculations?.count > 0 || wrongBookings.length > 0) && (
            <Paper sx={{ mt: 2, border: '1px solid #ffcdd2', borderRadius: 2 }}>
              <Box sx={{ px: 2, py: 1, bgcolor: '#ffebee', display: 'flex', alignItems: 'center', gap: 1 }}>
                <WarningIcon sx={{ color: '#c62828', fontSize: 18 }} />
                <Typography variant="subtitle2" fontWeight={600} sx={{ color: '#b71c1c' }}>Calculation audit ({report.wrong_calculations?.count ?? wrongBookings.length})</Typography>
              </Box>
              <TableContainer sx={{ maxHeight: 1000 }}>
                <Table size="small" stickyHeader>
                  <TableHead><TableRow><TableCell>Booking</TableCell><TableCell>Hotel</TableCell><TableCell>Issues</TableCell></TableRow></TableHead>
                  <TableBody>
                    {wrongBookings.map((b) => (
                      <TableRow key={b.booking_id || b.booking_code}>
                        <TableCell>{b.booking_code}</TableCell>
                        <TableCell>{b.hotel_name || '–'}</TableCell>
                        <TableCell><Typography variant="caption">{(b.wrong_calculation?.issues || []).join(', ')}</Typography></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </>
      )}

      <Dialog open={filterDialogOpen} onClose={() => setFilterDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Filters</DialogTitle>
        <DialogContent dividers>
          <FormControlLabel control={<Checkbox checked={draftPayAtHotel} onChange={(e) => setDraftPayAtHotel(e.target.checked)} />} label="Include Pay at Hotel bookings" sx={{ mb: 2 }} />
          {(optionalFilterDefs.length ? optionalFilterDefs : [
            { key: 'settlement_status', label: 'Settlement status', options: ['transferred', 'pending', 'failed', 'none'] },
            { key: 'booking_status', label: 'Booking status', options: ['Confirmed', 'Cancelled', 'NoShow', 'Pending'] },
          ]).map((fd) => renderFilterGroup(fd.key, fd.label, fd.options, draftFilters, setDraftFilters))}
          <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 2, mb: 1 }}>Report sections</Typography>
          <Grid container>{Object.entries(INCLUDE_LABELS).map(([key, label]) => (
            <Grid item xs={12} sm={6} key={key}>
              <FormControlLabel control={<Checkbox size="small" checked={key === 'booking_sections' ? true : !!draftInclude[key]} disabled={key === 'booking_sections'} onChange={() => setDraftInclude((p) => ({ ...p, [key]: !p[key] }))} />} label={<Typography variant="body2">{label}</Typography>} />
            </Grid>
          ))}</Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDraftFilters(emptySelectedFilters()); setDraftInclude({ ...DEFAULT_INCLUDE_SECTIONS }); setDraftPayAtHotel(false); }}>Reset</Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setFilterDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => { setSelectedFilters({ ...draftFilters }); setIncludeSections({ ...draftInclude, booking_sections: true }); setIncludePayAtHotel(draftPayAtHotel); setFilterDialogOpen(false); }} sx={{ bgcolor: '#FF7700' }}>Apply</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={excelDialogOpen} onClose={() => !exporting && setExcelDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Export Excel</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Main sheet is <strong>All Bookings</strong> — every booking with money, settlement,
            <strong> GST number</strong>, and <strong>Room GST filed by</strong> (TabTrips / Hotel).
            Sorted by check-out date.
          </Typography>
          <Grid container>
            {Object.keys(DEFAULT_EXCEL_SECTIONS).map((key) => (
              <Grid item xs={12} sm={6} key={key}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!excelSections[key]}
                      onChange={() => setExcelSections((p) => ({ ...p, [key]: !p[key] }))}
                    />
                  }
                  label={<Typography variant="body2">{EXCEL_SECTION_LABELS[key] || key}</Typography>}
                />
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExcelDialogOpen(false)} disabled={exporting}>Cancel</Button>
          <Button variant="contained" onClick={handleExcelExport} disabled={exporting} sx={{ bgcolor: '#2e7d32' }}>{exporting ? 'Exporting…' : 'Download'}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

function normalizeOptions(items) {
  if (!Array.isArray(items)) return [];
  const seen = new Set();
  return items.reduce((out, item) => {
    let value, label;
    if (typeof item === 'string' || typeof item === 'number') { value = item; label = String(item); }
    else if (item && typeof item === 'object') {
      value = item.value ?? item.key ?? item.id;
      label = item.label ?? item.name ?? String(value ?? '');
    } else return out;
    if (value == null || value === '') return out;
    const sk = String(value);
    if (seen.has(sk)) return out;
    seen.add(sk);
    out.push({ value, label });
    return out;
  }, []);
}

function renderFilterGroup(key, label, options, state, setState) {
  const opts = normalizeOptions(options);
  if (!opts.length) return null;
  return (
    <Box key={key} sx={{ mb: 1.5 }}>
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>{label}</Typography>
      <FormGroup row>
        {opts.map((o) => (
          <FormControlLabel
            key={`${key}-${o.value}`}
            control={<Checkbox size="small" checked={(state[key] || []).includes(o.value)} onChange={() => setState((prev) => {
              const arr = prev[key] || [];
              return { ...prev, [key]: arr.includes(o.value) ? arr.filter((x) => x !== o.value) : [...arr, o.value] };
            })} />}
            label={<Typography variant="body2">{o.label}</Typography>}
          />
        ))}
      </FormGroup>
    </Box>
  );
}

export default FinanceReport;
